import type LayerViewModel from "../map/LayerViewModel";
import type { ILayerView } from "../model/Layer";
import type ColorUtil from "./colorUtil";
import type SalesforceUtil from "./salesforceUtil";
import type Layer from "@esWebAPI/es/map/module/Layer";
import type { Map } from "@esWebAPI/es/map"

const es = global.es; // TODO Remove dependency

export default class MapUtil {
    private readonly colorUtil: ColorUtil;
    private readonly salesforceUtil: SalesforceUtil;

    constructor(colorUtil: ColorUtil, salesforceUtil: SalesforceUtil) {
        this.colorUtil = colorUtil;
        this.salesforceUtil = salesforceUtil;

        this.getLayerOfType = this.getLayerOfType.bind(this);
        this.getMapScopeCustomPointLayerViewModel = this.getMapScopeCustomPointLayerViewModel.bind(this);
        this.getMapScopeCustomAreaLayerViewModel = this.getMapScopeCustomAreaLayerViewModel.bind(this);
        this.getMapScopeRadiusLayerViewModel = this.getMapScopeRadiusLayerViewModel.bind(this);
        this.getMapScopeDriveTimeLayerViewModel = this.getMapScopeDriveTimeLayerViewModel.bind(this);
        this.isMapScopeLayerViewPartOfAnalysis = this.isMapScopeLayerViewPartOfAnalysis.bind(this);
        this.addRadiusBufferLayerview = this.addRadiusBufferLayerview.bind(this);
        this.addDriveTimeBufferLayerview = this.addDriveTimeBufferLayerview.bind(this);
        this.isMapScopeLayerView = this.isMapScopeLayerView.bind(this);
        this.getEditableLayerViewIds = this.getEditableLayerViewIds.bind(this);
        this.getSalesforceLayerViewIds = this.getSalesforceLayerViewIds.bind(this);
        this.isContainSalesforceLayerViews = this.isContainSalesforceLayerViews.bind(this);
    }

    /**
     * 
     * @param layers 
     * @param type 
     * 
     * @returns 
     */
    getLayerOfType(layers: LayerViewModel[], type: string): LayerViewModel | undefined {
        return layers.find((layer) => layer.getType() === type);
    }

    /**
     * Get the MapScopeCustomPointLayerView AddressPoints
     *
     * @param layerModule
     *
     * @return the LayerViewModel or null if not found
     */
    getMapScopeCustomPointLayerViewModel(layerModule: Layer): LayerViewModel | undefined {
        return this.getLayerOfType(layerModule.list(), "MapScopeCustomPointLayerView");
    }

    /**
     * Get the MapScopeCustomAreaLayerView CustomAreas
     *
     * @param layerModule
     *
     * @return the LayerViewModel or null if not found
     */
    getMapScopeCustomAreaLayerViewModel(layerModule: Layer): LayerViewModel | undefined {
        return this.getLayerOfType(layerModule.list(), "MapScopeCustomAreaLayerView");
    }

    /**
     * Get the MapScopeRadiusLayerView Radius
     *
     * @param layerModule
     *
     * @return the LayerViewModel or null if not found
     */
    getMapScopeRadiusLayerViewModel(layerModule: Layer): LayerViewModel | undefined {
        return this.getLayerOfType(layerModule.list(), "MapScopeRadiusLayerView");
    }

    /**
     * Get the MapScopeDriveTimeLayerView DriveTime
     *
     * @param layerModule
     *
     * @return the LayerViewModel or null if not found
     */
    getMapScopeDriveTimeLayerViewModel(layerModule: Layer): LayerViewModel | undefined {
        return this.getLayerOfType(layerModule.list(), "MapScopeDriveTimeLayerView");
    }

    /**
     * 
     * @param Type 
     * @param layerModule 
     * @param analysis 
     * 
     * @returns 
     */
    isMapScopeLayerViewPartOfAnalysis(Type: string, layerModule: Layer, analysis: any): boolean {
        let layerViewModel: LayerViewModel | undefined;
        if (Type === "POINT") {
            layerViewModel = this.getMapScopeCustomPointLayerViewModel(layerModule);
        } else if (Type === "POLYGON") {
            layerViewModel = this.getMapScopeCustomAreaLayerViewModel(layerModule);
        } else if (Type === "RADIUS") {
            layerViewModel = this.getMapScopeRadiusLayerViewModel(layerModule);
        } else if (Type === "DRIVE_TIME") {
            layerViewModel = this.getMapScopeDriveTimeLayerViewModel(layerModule);
        }

        if (layerViewModel) {
            const sourceLayerViewIds = es.api.analysis.getSourceLayerViewIds(analysis);
            if (sourceLayerViewIds.includes(layerViewModel.getId())) {
                return true;
            }
        }

        return false;
    }

    /**
     * 
     * @param geoMapId 
     * @param color 
     * 
     * @returns 
     */
    addRadiusBufferLayerview(geoMapId: number, color: number, colorPerFeature?: boolean): JQueryDeferred<ILayerView> {
        const colorWithAlpha = this.colorUtil.withAlpha(color, 90);
        return es.api.layerView.create({
            "$type":"MapScopeRadiusLayerView",
            "id":-1,
            "title":"Radius Buffer",
            "geoMapId" : geoMapId,
            "geomType": "POLYGON",
            "enabled": true,
            "ordering" : -1,
            "dynamicAttributes": [],
            "styleSettings": {
                "$type":"PolygonStyleSettings",
                "defaultStyle": {
                    "$type": "PolygonStyle",
                    "fillColor": colorWithAlpha,
                    "line": {
                        "color": color,
                        "width": 1,
                        "join": 1,
                        "endCap": 1
                    },
                    "text": null,
                    "vertex": null
                },
                "labelSettings": {
                    "labelling": "NON_OVERLAPPING",
                    "labelTemplate": "{{NAME}}",
                    "enabled": false
                },
                ...(colorPerFeature ? {"colorPerFeatureSettings": {"enabled": true}} : {})
            }
        });
    };

    /**
     * 
     * @param geoMapId 
     * @param color 
     * 
     * @returns 
     */
    addDriveTimeBufferLayerview(geoMapId: number, color: number, colorPerFeature?: boolean): JQueryDeferred<ILayerView> {
        const colorWithAlpha = this.colorUtil.withAlpha(color, 90);
        return es.api.layerView.create({
            "$type":"MapScopeDriveTimeLayerView",
            "id":-1,
            "title":"Drive Time Buffer",
            "geoMapId" : geoMapId,
            "geomType": "POLYGON",
            "enabled": true,
            "ordering" : -1,
            "dynamicAttributes": [],
            "styleSettings": {
                "$type":"PolygonStyleSettings",
                "defaultStyle": {
                    "$type": "PolygonStyle",
                    "fillColor": colorWithAlpha,
                    "line": {
                        "color": color,
                        "width": 1,
                        "join": 1,
                        "endCap": 1
                    },
                    "text": null,
                    "vertex": null
                },
                "labelSettings": {
                    "labelling": "NON_OVERLAPPING",
                    "labelTemplate": "{{NAME}}",
                    "enabled": false
                },
                ...(colorPerFeature ? {"colorPerFeatureSettings": {"enabled": true}} : {})
            }
        });
    };

    /**
     * 
     * @param type 
     * 
     * @returns 
     */
    isMapScopeLayerView(type: string): boolean {
        return type === 'MapScopeCustomPointLayerView'
            || type === 'MapScopeCustomAreaLayerView'
            || type === 'MapScopeRadiusLayerView'
            || type === 'MapScopeDriveTimeLayerView';
    };

    /**
     * Get editable layerViews ids in the existing map

     * @param map to check
     *
     * @returns Promise<[editable layer views ids]>
     */
    getEditableLayerViewIds(map: Map): JQueryDeferred<any[]> {
        const isEditableDfds = map.layer.getLayerViewIds().filter(function(id) {
            return map.layer.getById(id).isSelectable();
        }).map(function(id) {
            return es.api.security.isEditableLayerView(id).then(function(isEditable) {
                return es.util.deferred.resolved({'id': id, 'isEditable': isEditable});
            });
        });
        
        return $.when.apply($, isEditableDfds).then(function() {
            return Array.prototype.reduce.call(arguments, function(a, e) {
                if (e.isEditable) {
                    a.push(e.id);
                }
                
                return a;
            }, []);
        });
    }
    
    /**
     * Get salesforce layer view ids
     * 
     * @param map 
     * 
     * @returns 
     */
    getSalesforceLayerViewIds(map: Map): number[] {
        return map.layer.getLayerViewIds().filter((id) => {
            return this.salesforceUtil.isSfImportMeta(map.layer.getById(id).getLayerView());
        });
    }

    /**
     * 
     * @param map 
     * 
     * @returns 
     */
    isContainSalesforceLayerViews(map: Map): boolean {
        return (!!map.layer.list().find((layerViewModel) => {
            return this.salesforceUtil.isSfImportMeta(layerViewModel.getLayerView());
        }));
    }
}
